import React from 'react';
import { CurrencyCircleDollar } from '@phosphor-icons/react';
import { useDashboardV3Data } from './useDashboardV3Data';

export type DashboardV3PricingFinanceSectionProps = {
    onOpenDealHealth: () => void;
    onOpenPricingOptions: () => void;
    onEditLine: (lineId: string) => void;
};

const PRODUCT_TAG_CLASS: Record<string, string> = {
    Solar: 'solar',
    Roof: 'roof',
    Battery: 'battery',
    'Solar + Roof': 'solar',
    'Solar + Battery': 'solar',
    'Battery + Roof': 'battery',
};

export const DashboardV3PricingFinanceSection: React.FC<DashboardV3PricingFinanceSectionProps> = ({
    onOpenDealHealth,
    onOpenPricingOptions,
    onEditLine,
}) => {
    const { financeSummary } = useDashboardV3Data();
    const totalDisplay =
        financeSummary.totalMonthly > 0
            ? `$${financeSummary.totalMonthly.toFixed(2)}/mo`
            : '—';

    const breakdown =
        financeSummary.lines.length > 0
            ? financeSummary.lines
                  .map((l) => `${l.partner} $${l.monthly.toFixed(2)}`)
                  .join(' + ')
            : 'Select financing options in the pricing grid';

    return (
        <section className="obd-dashboard-v3-layout__pricing obd-dashboard-v3-layout__pricing--combined" aria-label="Pricing and finance">
            <h2 className="obd-dashboard-v3-layout__section-title">Pricing &amp; Finance</h2>
            <div className="obd-v3-pricing-card">
                <div className="obd-v3-pricing-card__main">
                    <div className="obd-v3-pricing-card__combined-head">
                        <div>
                            <p className="obd-v3-pricing-card__combined-label">Combined Financing</p>
                            <p className="obd-v3-pricing-card__combined-total">{totalDisplay}</p>
                            <p className="obd-v3-pricing-card__combined-breakdown">{breakdown}</p>
                        </div>
                        <button
                            type="button"
                            className="obd-v3-pricing-card__deal-health"
                            onClick={onOpenDealHealth}
                            title="Open deal health and pricing"
                        >
                            <span className="obd-v3-pricing-card__deal-health-label">Deal Health</span>
                            <span className="obd-v3-pricing-card__deal-health-value">{totalDisplay}</span>
                        </button>
                    </div>

                    {financeSummary.productTags.length > 0 ? (
                        <div className="obd-v3-pricing-card__tags">
                            {financeSummary.productTags.map((tag) => (
                                <span
                                    key={tag}
                                    className={`obd-v3-pricing-card__tag obd-v3-pricing-card__tag--${PRODUCT_TAG_CLASS[tag] ?? 'neutral'}`}
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                    ) : null}

                    <div className="obd-v3-pricing-card__lines">
                        {financeSummary.lines.length === 0 ? (
                            <p className="obd-v3-pricing-card__empty">
                                Select cells in the financing grid to build a combined monthly payment.
                            </p>
                        ) : (
                            financeSummary.lines.map((line) => (
                                <div key={line.id} className="obd-v3-pricing-card__line">
                                    <div className="obd-v3-pricing-card__line-head">
                                        <span className="obd-v3-pricing-card__line-icon" aria-hidden>
                                            <CurrencyCircleDollar size={18} weight="duotone" />
                                        </span>
                                        <div>
                                            <p className="obd-v3-pricing-card__line-title">
                                                {line.partner}
                                                {line.method ? ` — ${line.method}` : ''}
                                            </p>
                                            <p className="obd-v3-pricing-card__line-monthly">
                                                ${line.monthly.toFixed(2)}/mo
                                            </p>
                                        </div>
                                    </div>
                                    <div className="obd-v3-pricing-card__line-actions">
                                        <button
                                            type="button"
                                            className="obd-v3-pricing-card__line-link"
                                            onClick={onOpenPricingOptions}
                                        >
                                            View details
                                        </button>
                                        <button
                                            type="button"
                                            className="obd-v3-pricing-card__line-link"
                                            onClick={() => onEditLine(line.id)}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <button type="button" className="obd-v3-pricing-card__compare" onClick={onOpenPricingOptions}>
                        Compare all pricing &amp; financing options
                    </button>
                </div>
            </div>
        </section>
    );
};
